import Decimal from "decimal.js";
import * as investment_reo_calculator from "./investment_reo";

export function year_to_month_income(amount) {
  return new Decimal(amount).dividedBy(12);
}

export function month_to_year_income(amount) {
  return new Decimal(amount).times(12);
}

export function payment_to_year(amount, payments) {
  return new Decimal(amount).times(payments);
}

export function year_to_payment(amount, payments) {
  return new Decimal(amount).dividedBy(payments);
}

export function two_year_average(amount1, amount2) {
  return new Decimal(amount1).plus(new Decimal(amount2)).dividedBy(2);
}

//TODO make this more generic so we don't have to always include all of the fields.
export function yearly_income_total(base, overtime, bonuses, commission, tips) {
  return new Decimal(base)
    .plus(overtime)
    .plus(bonuses)
    .plus(commission)
    .plus(tips || 0);
}

export function percent_increase_income(monthly_income, increase) {
  return new Decimal(monthly_income)
    .times(new Decimal(increase))
    .dividedBy(100)
    .plus(new Decimal(monthly_income));
}

export function sum_total_other_income(other_income) {
  return other_income.reduce((accum, entry) => {
    return accum.plus(new Decimal(entry.yearly_amount));
  }, new Decimal(0));
}

export function sum_total_income_sources(income_sources) {
  if (income_sources === null || income_sources === undefined) {
    return new Decimal(0);
  }
  return income_sources.reduce(
    (accum, source) => accum.add(income_source_yearly_amount(source)),
    new Decimal(0)
  );
}

export function compute_total_income(borrowers, investment_properties) {
  const totalPositiveInvestREOIncome = investment_properties_yearly_amount(
    investment_properties
  );
  const totalIncome = borrowers.reduce((accum, borrower) => {
    if (!borrower.income) {
      return accum;
    }
    return accum
      .plus(sum_total_income_sources(borrower.income.sources))
      .plus(sum_total_other_income(borrower.income.other_income));
  }, new Decimal(0));

  return totalIncome.add(
    totalPositiveInvestREOIncome.gt(0) ? totalPositiveInvestREOIncome : 0
  );
}

// Income used for DTI ratios ONLY. Excludes NonQM DSCR rents: a DSCR loan
// qualifies on the property's rent coverage (the DSCR ratio), NOT on DTI, so the
// DSCR "Rents Received" must never count as documented income in a DTI. A
// full-doc loan (Fannie Mae, government, or a full-doc/1099 Non-QM) in the same
// file must show DTI from documented income only - W-2/base, other income, and
// the Invest REO Schedule E net - so those are kept. Files without a DSCR source
// are unaffected (identical to compute_total_income).
export function compute_dti_income(borrowers, investment_properties) {
  const totalPositiveInvestREOIncome = investment_properties_yearly_amount(
    investment_properties
  );
  const totalIncome = borrowers.reduce((accum, borrower) => {
    if (!borrower.income) {
      return accum;
    }
    const nonDscrSources = (borrower.income.sources || []).filter(
      s => !(s && s.calculator === "NonQM DSCR")
    );
    return accum
      .plus(sum_total_income_sources(nonDscrSources))
      .plus(sum_total_other_income(borrower.income.other_income));
  }, new Decimal(0));

  return totalIncome.add(
    totalPositiveInvestREOIncome.gt(0) ? totalPositiveInvestREOIncome : 0
  );
}

export function investment_properties_monthly_amount(investment_properties) {
  const amount = investment_reo_calculator.net_income_or_loss_total(
    investment_properties
  );
  return amount.gt(0) ? amount : new Decimal(0);
}

export function investment_properties_yearly_amount(investment_properties) {
  return investment_properties_monthly_amount(investment_properties).mul(12);
}

/**
 * Income - Yearly Income
 */
/**
 * Bonus / Commission qualifying rule (Fannie + common underwriting): if the
 * current year is >= the prior year the income is stable or rising, so use the
 * CURRENT year; only when it declined do we fall back to the 2-year average.
 * (Some lenders' overlays take the lower current year instead - an LOE + an
 * underwriter conversation decides; see the Bonus/Commission training tip.)
 */
export function qualifying_variable_income(current, prior) {
  const cur = new Decimal(current || 0);
  const pri = new Decimal(prior || 0);
  return cur.gte(pri) ? cur : cur.add(pri).dividedBy(2);
}

/**
 * The MISMO income items to EXPORT for one employment source: Base plus each
 * variable component (Overtime, Bonus, Commissions, Tips) that is non-zero, each
 * as its own CURRENT_INCOME_ITEM with the correct MISMO IncomeType.
 *
 * Base = current year. Bonus/Commission = current-if-rising else 2-yr average
 * (qualifying_variable_income). Overtime/Tips = 2-yr average. These match
 * income_source_yearly_amount, so the item amounts sum to the qualifying income
 * (no double count). `offset` spaces the sequence numbers within the source
 * (sources are 10 apart) and is shared with the RELATIONSHIPS arc.
 */
export function income_export_components(source) {
  // NonQM DSCR: the qualifying income is the subject property's rental cash flow.
  // This is NOT "Net Rental Income" — that MISMO/POINT field is reserved for the
  // Schedule E net (tax-return) figure from the Invest REO page, used on Full-Doc
  // loans. DSCR rents have no dedicated MISMO IncomeType, so export them as an
  // "Other" income item with a description (POINT "Other Income") = total monthly
  // rents annualized.
  if (source && source.calculator === "NonQM DSCR") {
    return [
      {
        incomeType: "Other",
        otherDescription: "DSCR Rental Income",
        offset: 0,
        annual: dscr_total_monthly_rents(source).mul(12)
      }
    ];
  }
  const avg = (a, b) => new Decimal(a || 0).add(new Decimal(b || 0)).dividedBy(2);
  const all = [
    { incomeType: "Base", offset: 0, annual: new Decimal(source.base_income_0 || 0) },
    { incomeType: "Overtime", offset: 1, annual: avg(source.overtime_0, source.overtime_1) },
    { incomeType: "Bonus", offset: 2, annual: qualifying_variable_income(source.bonuses_0, source.bonuses_1) },
    { incomeType: "Commissions", offset: 3, annual: qualifying_variable_income(source.commission_0, source.commission_1) },
    { incomeType: "TipIncome", offset: 4, annual: avg(source.tips_0, source.tips_1) }
  ];
  // Always keep Base (the anchor item + arc for the job); drop zero variables.
  return all.filter(c => c.incomeType === "Base" || c.annual.gt(0));
}

export function dscr_total_monthly_rents(source) {
  const roll = (source && source.dscr_rent_roll) || [];
  return roll.reduce(
    (accum, unit) => accum.add(new Decimal((unit && unit.rent) || 0)),
    new Decimal(0)
  );
}

export function income_source_yearly_amount(source) {
  // NonQM DSCR: qualifying income = the subject property's total monthly rents
  // (100%) annualized. Rents ARE the borrower's qualifying income (the DSCR ratio
  // is the separate qualifier, shown in the calculator).
  if (source && source.calculator === "NonQM DSCR") {
    return dscr_total_monthly_rents(source).mul(12);
  }
  // Base = current year; Bonus/Commission = current-if-rising else 2-yr average
  // (qualifying_variable_income); Overtime/Tips = 2-yr average. Kept identical to
  // income_export_components so LANIS's qualifying income == what we send the LOS.
  return new Decimal(source.base_income_0)
    .add(new Decimal(source.overtime_0).add(source.overtime_1).div(2))
    .add(qualifying_variable_income(source.bonuses_0, source.bonuses_1))
    .add(qualifying_variable_income(source.commission_0, source.commission_1))
    .add(new Decimal(source.tips_0 || 0).add(source.tips_1 || 0).div(2));
}
